import React, { useState, useMemo } from "react";
import { Calculator, TrendUp } from "@phosphor-icons/react";

// Named slider input with label + big value display
function Slider({ label, value, onChange, min, max, step, format, testid }) {
  return (
    <div className="mb-6">
      <div className="flex items-baseline justify-between mb-2">
        <label className="label-base" style={{ marginBottom: 0 }}>{label}</label>
        <span className="font-display font-black text-2xl tracking-tighter" style={{ color: "var(--accent)" }}>
          {format ? format(value) : value}
        </span>
      </div>
      <input
        type="range" min={min} max={max} step={step}
        value={value} onChange={(e) => onChange(Number(e.target.value))}
        data-testid={testid}
        className="w-full"
        style={{ accentColor: "var(--accent)" }}
      />
      <div className="flex justify-between text-xs mt-1" style={{ color: "var(--text-3)" }}>
        <span>{format ? format(min) : min}</span>
        <span>{format ? format(max) : max}</span>
      </div>
    </div>
  );
}

export default function ROICalculator() {
  // Inputs — default numbers modeled on the "typical operator" persona
  const [events, setEvents] = useState(4);          // per month
  const [cardsPerEvent, setCardsPerEvent] = useState(10);
  const [currentFollowup, setCurrentFollowup] = useState(20); // %
  const [improvedFollowup, setImprovedFollowup] = useState(80); // %
  const [closeRate, setCloseRate] = useState(5);    // %
  const [clientValue, setClientValue] = useState(2500); // $ per client

  const result = useMemo(() => {
    const cardsPerMonth = events * cardsPerEvent;
    const convBefore = Math.round(cardsPerMonth * (currentFollowup / 100));
    const convAfter = Math.round(cardsPerMonth * (improvedFollowup / 100));
    const extraConv = Math.max(convAfter - convBefore, 0);
    const extraClients = extraConv * (closeRate / 100);
    const extraMonthlyRev = extraClients * clientValue;
    const extraAnnualRev = extraMonthlyRev * 12;
    return {
      cardsPerMonth,
      convBefore, convAfter, extraConv,
      extraClients: Number(extraClients.toFixed(1)),
      extraMonthlyRev: Math.round(extraMonthlyRev),
      extraAnnualRev: Math.round(extraAnnualRev),
    };
  }, [events, cardsPerEvent, currentFollowup, improvedFollowup, closeRate, clientValue]);

  const fmt$ = (n) => "$" + n.toLocaleString("en-US");
  const fmtPct = (n) => `${n}%`;

  return (
    <div className="border" style={{ borderColor: "var(--accent)", background: "rgba(45,212,212,0.04)" }} data-testid="roi-calculator">
      <div className="p-6 md:p-8 border-b" style={{ borderColor: "var(--accent)" }}>
        <div className="flex items-center gap-3">
          <Calculator size={28} weight="duotone" color="var(--accent)" />
          <div className="overline" style={{ color: "var(--accent)" }}>// interactive ROI calculator</div>
        </div>
        <h3 className="font-display font-black text-3xl md:text-4xl tracking-tighter leading-none mt-3">
          Do the math on <span style={{ color: "var(--accent)" }}>your</span> pipeline.
        </h3>
        <p className="mt-3 text-sm" style={{ color: "var(--text-2)" }}>Move the sliders. Every number updates live. Every value defaults to industry averages — override with your own.</p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-12">
        {/* Inputs */}
        <div className="lg:col-span-7 p-6 md:p-8 border-r" style={{ borderColor: "var(--accent)" }}>
          <div className="overline mb-6">// your inputs</div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8">
            <Slider label="Events / month" value={events} onChange={setEvents} min={0} max={20} step={1} testid="roi-events" />
            <Slider label="Cards / event" value={cardsPerEvent} onChange={setCardsPerEvent} min={0} max={100} step={1} testid="roi-cards" />
            <Slider label="Current follow-up rate" value={currentFollowup} onChange={setCurrentFollowup} min={0} max={100} step={5} format={fmtPct} testid="roi-current" />
            <Slider label="With My Nexus Card" value={improvedFollowup} onChange={setImprovedFollowup} min={0} max={100} step={5} format={fmtPct} testid="roi-improved" />
            <Slider label="Your close rate" value={closeRate} onChange={setCloseRate} min={1} max={50} step={1} format={fmtPct} testid="roi-close" />
            <Slider label="Avg client value" value={clientValue} onChange={setClientValue} min={100} max={100000} step={100} format={fmt$} testid="roi-value" />
          </div>
        </div>

        {/* Outputs */}
        <div className="lg:col-span-5 p-6 md:p-8">
          <div className="overline mb-6">// your outcome</div>

          <div className="grid grid-cols-2 gap-0 border-l border-t" style={{ borderColor: "var(--border)" }}>
            <div className="border-r border-b p-4" style={{ borderColor: "var(--border)" }}>
              <div className="overline">before</div>
              <div className="font-display font-black text-3xl mt-1" style={{ color: "var(--text-2)" }} data-testid="out-before">{result.convBefore}</div>
              <div className="overline mt-1">conversations/mo</div>
            </div>
            <div className="border-r border-b p-4" style={{ borderColor: "var(--border)" }}>
              <div className="overline" style={{ color: "var(--accent)" }}>after</div>
              <div className="font-display font-black text-3xl mt-1" style={{ color: "var(--accent)" }} data-testid="out-after">{result.convAfter}</div>
              <div className="overline mt-1">conversations/mo</div>
            </div>
            <div className="border-r border-b p-4" style={{ borderColor: "var(--border)" }}>
              <div className="overline">extra clients</div>
              <div className="font-display font-black text-3xl mt-1" data-testid="out-clients">+{result.extraClients}</div>
              <div className="overline mt-1">per month</div>
            </div>
            <div className="border-r border-b p-4" style={{ borderColor: "var(--border)" }}>
              <div className="overline">extra revenue</div>
              <div className="font-display font-black text-3xl mt-1" data-testid="out-rev-monthly">{fmt$(result.extraMonthlyRev)}</div>
              <div className="overline mt-1">per month</div>
            </div>
          </div>

          <div className="mt-6 p-5 border" style={{ borderColor: "var(--accent)", background: "rgba(45,212,212,0.08)" }}>
            <div className="overline mb-2 flex items-center gap-2" style={{ color: "var(--accent)" }}>
              <TrendUp weight="bold" /> projected annual upside
            </div>
            <div className="font-display font-black text-5xl md:text-6xl tracking-tighter" style={{ color: "var(--accent)" }} data-testid="out-rev-annual">
              {fmt$(result.extraAnnualRev)}
            </div>
            <p className="text-xs mt-3" style={{ color: "var(--text-2)" }}>
              Based on {result.extraConv} extra live conversations/month × {closeRate}% close × ${clientValue.toLocaleString()} per client × 12 months.
            </p>
            <a href="https://calendly.com/georgeatabora/30min" target="_blank" rel="noreferrer" data-testid="roi-calendly-cta" className="btn-primary mt-5 w-full flex items-center justify-between">
              Book a call to hit these numbers <TrendUp weight="bold" />
            </a>
          </div>

          <p className="text-xs mt-6" style={{ color: "var(--text-3)" }}>
            Model is directional, not a guarantee. Real-world close rate and client value depend on your offer, market, and follow-through discipline. My Nexus Card automates the follow-up — closing is still your job.
          </p>
        </div>
      </div>
    </div>
  );
}
